<template>
    <div :id="rootElementId" :style="`height:${SVG_RENDER_HEIGHT}px`"></div>
</template>

<script>

import { getDataUrl,getNeutralColors } from '../../utils/functions';
import { loadFonts,copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import Dictionary from '../../dictionary';

export default {
  name: 'XAChart',
  props: {
    id:{
      type: String,
      required: true
    },
    size: {
      type: Number,
      required: false,
    },
    margin: {
      type: Object,
      // {top: 0, right: 0, bottom: 0, left: 0}
      required: false,
    },
    type : {
      type: String,
      required: true,
    },
    passes: {
      type: Array,
      required: true,
    },
    playerName: {
      type: String,
      required: false
    },
    seasons: {
      type: Array,
      required: false
    },
    game: {
      type: Object,
      required: false
    },
    circlesAndBoxes: {
      type: Array,
      required: false
    },
    language: {
      type: String,
      required: false
    },
    homeFillColor:{
      type: String,
      default: "#ffffff"
    },
    awayFillColor:{
      type: String,
      default: "#ffffff"
    },
    homeBorderColor:{
      type: String,
      default: "#ffffff"
    },
    awayBorderColor:{
      type: String,
      default: "#ffffff"
    },
    mediaMode: {
      type: Boolean,
      default: false
    },
    postGameAnalysis:{
      type: Boolean,
      default: false
    },
    homeTeamName:{
      type: String,
      default: ""
    },
    awayTeamName:{
      type: String,
      default: ""
    },
    team:{
      type: Boolean,
      default: false
    },
    teamOrOpponent: { // At team level we can also show opponent passes: This will be true if showing team and false if showing opponent (Texts change if showing opponent stats)
      type: Boolean,
      default: true
    },
    //gameId for print one game Player xA Map
    gameId:{
      type:Number,
      default: 0
    },
    //gameIds for print games of month Team xA Map
    gameIds: {
      type: Array,
      required: false,
      default: () => []
    },
    gameName:{
      type: String,
      default: ""
    },
    fontFamily:{
      type: String,
      default: 'NeueMontreal'
    },
    minsPlayed: {
      type: Number,
      default: 0,
    },
    teamId: {
      type: Number,
      default: 0
    },
    filterShotTypeText: {
      type: String,
      default: ''
    },
    filterShotStrategyText: {
      type: String,
      default: ''
    },
    filterBodyPartText: {
      type: String,
      default: ''
    },
    filterMinutesText: {
      type: String,
      default: ''
    },
    filterTimeText: {
      type: String,
      default: ''
    },
    filterMinutesRange: {
      type: String,
      default: ''
    },
    orientation: {
      type: String,
      default: 'horizontal'
    }
  },
  data() {
    return {
      height: 0,
      width: 0,
      images: {}
    }
  },
  computed: {
    SVG_WIDTH(){
      return this.size ? this.size : 380;
    },
    PITCH_WIDTH(){
      if(this.margin){
        return this.SVG_WIDTH - this.margin.left - this.margin.right
      }else{
        return this.SVG_WIDTH;
      }
    },
    PITCH_HEIGHT() {
      return this.game ? this.PITCH_WIDTH * 0.67125 : this.PITCH_WIDTH * 0.755
    },
    isVertical() {
      return this.orientation === 'vertical';
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown || this.$vuetify.breakpoint.mobile;
    },
    SVG_RENDER_WIDTH() {
      return this.isVertical ? this.PITCH_HEIGHT : this.SVG_WIDTH;
    },
    SVG_RENDER_HEIGHT() {
      return this.isVertical ? this.SVG_WIDTH : this.PITCH_HEIGHT;
    },
    rootElementId(){
      return `pitchXA${this.id}`
    },
    currentUserLanguage() {
      return this.$store.getters["auth/language"];
    },
    currentUserFieldColor() {
      return this.$store.getters["auth/fieldColor"];
    },
    currentUserAreas(){
      let layoutId = this.$store.getters["auth/fieldLayout"]
      return _.chain(this.layouts).find(l => l.id === layoutId).get("areas").reduce((allAreas,a) => {
        allAreas.push(a);
        return allAreas;
      },[]).value();
    },
    layouts(){
      return this.$store.getters["general/fieldLayouts"];
    }
  },
  mounted() {
    this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
    this.printD3();
  },
  methods: {
    async printD3(force = false){
      if(force){
        this.images = {};
      }

      const SVG_WIDTH = this.SVG_WIDTH;
      const margin = this.margin ? this.margin : {top: 0, right: 0, bottom: 0, left: 0};
      const zeroMargin = {top: 0, right: 0, bottom: 0, left: 0};

      const PITCH_WIDTH = this.PITCH_WIDTH;
      const PITCH_HEIGHT = this.PITCH_HEIGHT;
      const renderW = this.isVertical ? PITCH_HEIGHT : PITCH_WIDTH;
      const renderH = this.isVertical ? PITCH_WIDTH : PITCH_HEIGHT;

      const textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';

      d3.selectAll('.tooltip').remove();

      d3.select(`#${this.rootElementId}`).select("svg").remove();

      const tooltip = d3
        .select("body")
        .append("div")
        .attr("class", "tooltip");

      var svg = d3.select(`#${this.rootElementId}`)
        .append("svg")
        .attr("width", this.isVertical ? PITCH_HEIGHT : SVG_WIDTH)
        .attr("height", this.isVertical ? SVG_WIDTH : PITCH_HEIGHT)
        .style("background-color", "#ffffff")
        .attr("id",`${this.rootElementId}SVG`)
        .attr("class", "pitch");

      const pitchLayer = svg.append("g").attr("transform",
        this.isVertical
          ? `translate(${margin.left},${margin.top + PITCH_WIDTH}) rotate(-90)`
          : `translate(${margin.left},${margin.top})`
      );
      const uiLayer = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

      // DRAW PITCH
      this.drawImageBase64(pitchLayer, 0, 0, PITCH_WIDTH, PITCH_HEIGHT, require(`@/assets/img/${this.type === "game" ? "": "half_"}pitch_${this.currentUserFieldColor}.png`),'pitch',true);

      let firstHomeTeamColor = this.homeFillColor;
      let secondHomeTeamColor = this.homeBorderColor;
      let neutralColorsAway = getNeutralColors('away');
      let firstAwayTeamColor = this.awayFillColor;
      let secondAwayTeamColor = this.awayBorderColor;
      //If is game xA Map but it's post game, don't get team colors, keep neutral colors
      if (this.type === "game" && !this.postGameAnalysis && !this.mediaMode) {
        firstHomeTeamColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor : (this.game.homeTeamColor ? this.game.homeTeamColor : firstHomeTeamColor);
        secondHomeTeamColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor : (this.game.homeTeamSecondColor ? this.game.homeTeamSecondColor : secondHomeTeamColor);
        firstAwayTeamColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor : (this.game.awayTeamColor ? this.game.awayTeamColor : firstAwayTeamColor);
        secondAwayTeamColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor : (this.game.awayTeamSecondColor ? this.game.awayTeamSecondColor : secondAwayTeamColor);
      }
      if(this.type === "game" && firstHomeTeamColor === firstAwayTeamColor){
        firstAwayTeamColor = neutralColorsAway[0];
      }
      if(this.type === "game" && secondHomeTeamColor === secondAwayTeamColor){
        secondAwayTeamColor = neutralColorsAway[1];
      }

      let logo  = require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_${this.currentUserFieldColor === 'white' ? 'color' : 'white'}.png`);
   
      if (this.type === "player") {
        // Key pass / assist probability legend at top-left
        this.drawImageBase64(svg, margin.left + PITCH_WIDTH * 0.07, margin.top + PITCH_WIDTH * 0.012, PITCH_WIDTH * 0.36, PITCH_HEIGHT * 0.08,
        require(`@/assets/img/legend_xa${this.currentUserFieldColor === 'white' ? "_dark": ""}.png`),"pitchLegend");

        let keyPassText = this.$t('event.KeyPass').toUpperCase().split(' ');
        this.writeText(svg, margin.left + PITCH_WIDTH * 0.17, margin.top + PITCH_WIDTH * 0.029, "middle", (0.012 * PITCH_WIDTH) + "px", keyPassText[0], textColor);
        if(keyPassText.length > 1)
          this.writeText(svg, margin.left + PITCH_WIDTH * 0.17, margin.top + PITCH_WIDTH * 0.041, "middle", (0.012 * PITCH_WIDTH) + "px", keyPassText[1], textColor);
        
        let assistProbText = this.$t('general.assistProbability').toUpperCase().split(' ');
        this.writeText(svg, margin.left + PITCH_WIDTH * 0.37, margin.top + PITCH_WIDTH * 0.029, "middle", (0.012 * PITCH_WIDTH) + "px", assistProbText[0], textColor);
        if(keyPassText.length > 1)
          this.writeText(svg, margin.left + PITCH_WIDTH * 0.37, margin.top + PITCH_WIDTH * 0.041, "middle", (0.012 * PITCH_WIDTH) + "px", assistProbText[1], textColor);
        // Driblab Pro logo at top-right (matching XGChart positioning)
        const logoW = PITCH_WIDTH * 0.17;
        const logoH = PITCH_HEIGHT * 0.09;
        this.drawImageBase64(svg, margin.left + PITCH_WIDTH * 0.92 - logoW, margin.top + PITCH_WIDTH * 0.02 - 6, logoW, logoH, logo, "logoDriblab");

        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("circles")) {
          this.renderXAPassSegments(pitchLayer, 1, PITCH_WIDTH, PITCH_HEIGHT, this.passes, firstHomeTeamColor, this.type === "player");
          this.renderXACircles(pitchLayer, 1, PITCH_WIDTH, PITCH_HEIGHT, this.passes, firstHomeTeamColor, firstHomeTeamColor, tooltip, this.type === "player");
        }
        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("boxes")) {
          this.renderBoxes(pitchLayer,
            PITCH_WIDTH,
            PITCH_HEIGHT,
            zeroMargin,
            this.passes.filter(p => !p.penaltyGoal).filter(p => !p.ownGoal),
            this.language
          )
          if (this.isVertical) {
            pitchLayer.selectAll("text").each(function() {
              const el = d3.select(this);
              const x = +el.attr("x") || 0;
              const y = +el.attr("y") || 0;
              el.attr("transform", `rotate(90, ${x}, ${y})`);
            });
          }
        }
        await this.printPlayerXALegend(uiLayer, renderW, zeroMargin, this.passes, this.seasons, this.playerName, this.language);

      } else if (this.type === "game") {
        this.renderXAPassSegments(pitchLayer, 1, PITCH_WIDTH, PITCH_HEIGHT, this.passes, firstHomeTeamColor, this.type === "player");
        this.renderXACircles(pitchLayer, 1, PITCH_WIDTH, PITCH_HEIGHT, this.passes, firstHomeTeamColor, firstHomeTeamColor, tooltip, this.type === "player");
        this.printGameXALegend(uiLayer, renderW, renderH, zeroMargin, this.passes);
        this.printGameLegend(uiLayer, renderW, renderH, zeroMargin, firstHomeTeamColor, firstAwayTeamColor, this.game);
        this.printGameBottomLegend(uiLayer, renderW, renderH, zeroMargin, this.passes);
        const rectW = renderW * 0.58;
        const rectH = renderW * 0.1;
        const rectX = renderW / 2 - rectW / 2;
        const rectY = renderH - rectH - renderW * 0.01;
        const gap = renderW * 0.015;
        const legendW = renderW * (this.isVertical ? 0.16 : 0.12);
        const legendH = renderH * (this.isVertical ? 0.11 : 0.08);
        const legendY = rectY + rectH - legendH + renderW * (this.isVertical ? 0.03 : 0.008);
        this.drawImageBase64(uiLayer,
          rectX - legendW - gap,
          legendY,
          legendW,
          legendH,
          require(`@/assets/img/legend_xa${this.currentUserFieldColor === 'white' ? "_dark" : ""}_vertical.png`), 'legendPitchGame');

        //Key pass text
        this.writeText(svg, 
          rectX - legendW/2 - (PITCH_WIDTH * 0.015), 
          legendY + (PITCH_HEIGHT * (this.isVertical ? 0.03 : 0.02)), 
          "middle", 
          (0.008 * PITCH_WIDTH) + "px", 
          this.$t('event.KeyPass').toUpperCase(), 
          textColor);
        //Assist Probability text
        this.writeText(svg, 
        rectX - legendW/2 - (PITCH_WIDTH * 0.015), 
        legendY + legendH/2 + (PITCH_HEIGHT * 0.027), 
        "middle", 
        (0.008 * PITCH_WIDTH) + "px", 
        this.$t('general.assistProbability').toUpperCase(),
        textColor);
        
        const logoW = PITCH_WIDTH * (this.isVertical ? 0.1 : 0.14);
        const logoH = PITCH_HEIGHT * 0.07;
        const logoY = rectY + rectH - logoH;
        this.drawImageBase64(uiLayer,
          rectX + rectW + gap,
          logoY,
          logoW,
          logoH,
          logo, 'logoDriblab');
      }
      let font = await loadFonts(this.fontFamily);
      copyAndAppendElement(svg.node(),font.node(),0,0);

      //Emit when is loaded
      this.$emit("loaded", this.id);
    },
    measureText(string, fontSize = 10) {
      const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
        0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
        0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
        0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
        0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
        0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
        0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
        0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
      const avg = 0.5293256578947368;

      return string
        .split('')
        .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
        .reduce((cur, acc) => acc + cur) * fontSize;
    },
    truncateText(text, fontSize, maxWidth) {
      if (this.measureText(text, fontSize) <= maxWidth) return text;
      let t = text;
      while (this.measureText(t + '…', fontSize) > maxWidth && t.length > 1) {
        t = t.slice(0, -1);
      }
      return t + '…';
    },
    /** Final clamp for season lines: keep trailing " +N" (extra competitions) visible. */
    truncateSeasonLegendLine(line, fontSize, maxWidth) {
      const m = typeof line === 'string' && line.match(/^(.*)(\s\+\d+)$/);
      if (m && m[1].trim().length > 0) {
        const suffix = m[2];
        const mainTrim = m[1].replace(/\s+$/, '');
        const suffixW = this.measureText(suffix, fontSize);
        const maxMain = Math.max(0, maxWidth - suffixW);
        const main = this.truncateText(mainTrim, fontSize, maxMain);
        return main + suffix;
      }
      return this.truncateText(line, fontSize, maxWidth);
    },
    colorScale(minDomain, maxDomain, value, steps) {
        //Range of colors in HSV format gradient
        let colorsArray = ["#fb0e0e","#fb130e","#fb180f","#fa1d0f","#fa220f","#fa270f","#fa2c10","#fa3110","#f93610","#f93b11","#f94011","#f94511","#f84911","#f84e12","#f85312",
        "#f85812","#f85d13","#f76113","#f76613","#f76b13","#f76f14","#f77414","#f67914","#f67d14","#f68215","#f68715","#f58b15","#f59016","#f59416","#f59916","#f59d16","#f4a217",
        "#f4a617","#f4aa17","#f4af17","#f4b318","#f3b818","#f3bc18","#f3c019","#f3c519","#f3c919","#f2cd19","#f2d11a","#f2d61a","#f2da1a","#f1de1a","#f1e21b","#f1e61b","#f1ea1b",
        "#f1ee1b","#eef01c","#eaf01c","#e5f01c","#e1f01d","#dcf01d","#d8ef1d","#d4ef1d","#cfef1e","#cbef1e","#c6ee1e","#c2ee1e","#beee1f","#baee1f","#b5ee1f","#b1ed1f","#aded20",
        "#a9ed20","#a4ed20","#a0ed20","#9cec21","#98ec21","#94ec21","#90ec21","#8cec22","#88eb22","#84eb22","#80eb22","#7ceb23","#78ea23","#74ea23","#70ea23","#6cea24","#68ea24",
        "#64e924","#60e924","#5ce925","#58e925","#54e925","#51e825","#4de826","#49e826","#45e826","#42e726","#3ee727","#3ae727","#37e727","#33e727","#2fe628","#2ce628","#28e628"];

        //Get scale functions.
        // - Range: Number of gradient steps
        // - Domain: Range of values
        let colorScale = d3.scale.linear().domain([minDomain, maxDomain]).range([0, steps - 1]);
        //Get index of colorsArray
        let index = colorScale(value);
        //Get whole part of the index
        let color = colorsArray[Math.trunc(index)];

        return color;
    },
    renderXAPassSegments(svg, chartSizeRate, chartWidth, chartHeight, segments, teamColor, isPlayerXA) {

      let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
      let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);

      let radius = d3.scale.linear().domain([0, 1]).range([chartWidth * 0.0055, chartWidth * 0.024]);

      // ====================================================================
      //        Render Segments
      // ====================================================================
      for (let segment of segments) {
        let sx0;
        let sy0;
        let sx1;
        let sy1;
        if (isPlayerXA) {
          sx0 = x(segment.y);
          sy0 = y(100 - (100 - segment.x) * 2);
          sx1 = x(segment.y_end);
          sy1 = y(2 * segment.x_end - 100);
        } else if (segment.homeTeam) {
          sx0 = x(segment.x);
          sy0 = y(100 - segment.y);
          sx1 = x(segment.x_end);
          sy1 = y(100 - segment.y_end);
        } else {
          sx0 = x(100 - segment.x);
          sy0 = y(segment.y);
          sx1 = x(100 - segment.x_end);
          sy1 = y(segment.y_end);
        }
        this.drawXALine(svg, sx0, sy0, sx1, sy1, radius(segment.width) * 1.5, segment.xG, teamColor, 0.8);
      }
    },
    async drawImageBase64(svg,x,y,width,height,img,idName,bottom = false) {
      if(!this.images[idName]){
        let base_image = new Image();
        base_image.crossOrigin = "anonymous";
        base_image.src = img;
        base_image.onload = (event) => {
          const dataUrl = getDataUrl(event.currentTarget);
          this.drawImage(
          svg,
          x,
          y,
          width,
          height,
          dataUrl,
          idName,
          bottom
          );
        }
      }else{
        if(bottom){
          svg.node().insertBefore(this.images[idName],svg.node().firstChild);
        }else{
          svg.node().appendChild(this.images[idName]);
        }
      }
    },
    /** Same as drawImageBase64 but resolves after remote images are inlined (needed for PNG/SVG export). */
    drawImageBase64Async(svg, x, y, width, height, img, idName, bottom = false) {
      return new Promise((resolve) => {
        if (this.images[idName]) {
          if (bottom) {
            svg.node().insertBefore(this.images[idName], svg.node().firstChild);
          } else {
            svg.node().appendChild(this.images[idName]);
          }
          resolve();
          return;
        }
        const base_image = new Image();
        base_image.crossOrigin = "anonymous";
        base_image.onload = (event) => {
          const dataUrl = getDataUrl(event.currentTarget);
          this.drawImage(svg, x, y, width, height, dataUrl, idName, bottom);
          resolve();
        };
        base_image.onerror = () => resolve();
        base_image.src = img;
      });
    },
    drawImage: function(svg, x, y, width, height, img,idName,bottom = false) {
      let imgNode = bottom ? svg.insert("svg:image",':first-child') : svg.append("svg:image");
        imgNode.attr("x", x)
        .attr("y", y)
        .attr("width", width)
        .attr("height", height)
        .attr("xlink:href", img);
      this.images[idName] = imgNode.node().cloneNode(true);
    },
    drawRectangle(svg, x, y, width, height, color, opacity, className) {
      return svg.append("rect")
        .attr("class", className)
        .attr("x", x)
        .attr("y", y)
        .attr("width", width)
        .attr("height", height)
        .style("fill", color)
        .style("opacity", opacity);
    },
    writeText(svg, x, y, anchor, fontSize, text, fill = "white", className = "none") {
      svg.append("text")
        .attr("x", x)
        .attr("y", y)
        .attr("text-anchor", anchor)
        .attr("dy", "0.8em")
        .attr("font-weight", 500)
        .attr("class", className)
        .style("font-size", fontSize)
        .style("font-family", this.fontFamily)
        .style("fill", fill)
        .text(text);
    },
    getTypeOfShot(name) {
      if (name === "SAVED SHOT") return "Saved Shot";
      if (name === "GOAL") return "Goal";
      if (name === "MISSED SHOT") return "Missed Shot";
      if (name === "SHOT ON POST") return "Shot on Post";
      if (name === "MISSED CHANCE") return "Chance Missed";
      if (name === "Regular play") return "Regular Play";
      if (name && name.startsWith("Individual")) return "Individual Play";
      if (name === "Fast break") return "Fast Break";
      if (name === "From corner") return "From Corner";
      if (name === "Free kick") return "Free Kick";
      if (name === "Set piece") return "Set Piece";
      if (name && name.startsWith("Throw")) return "Throw In";
      if (name === "Left footed") return "Left Foot";
      if (name === "Right footed") return "Right Foot";
      if (name === "Head") return "Head";
      if (name === "Other body part") return "Other Body Part";
      if (name === "Keeper Throw") return "Keeper Throw";
      console.log("Unkown " + name);
      return "??";
    },
    getTypeOfPass(name) {
      if (name === "pass") return "Key Pass";
      if (name === "assist" || name === "goal") return "Assist";
      return "Key Pass";
    },
    drawXACircle(svg, chartSizeRate,  x, y, circle, i, radius, xa, tooltip, isAssist) {
      var self = this;
      svg.append("circle")
        .attr("r", radius * chartSizeRate)
        .attr("cx", x)
        .attr("cy", y)
        .attr('id', "pass" + i + "-" + circle.teamName)
        .style("fill-opacity", 0.6)
        .style("opacity", 0.8)
        .style("stroke",  isAssist ? "#000000" : "#" + this.colorScale(0.5, 0, xa > 0.5 ? 0.5 : xa, 100).substring(1))
        .style("stroke-width", 2 * chartSizeRate)
        .style("fill", "#" + this.colorScale(0.5, 0, xa > 0.5 ? 0.5 : xa, 100).substring(1))
        .on('mouseenter',function(d) {
          d3.select(this)
            .transition()
            .duration(300)
            .style("cursor", "pointer")
            .attr('r', radius * 1.75);

          var textLine1 = circle.game;
          var textLine2 = self.getTypeOfPass(circle.type) + " to "+ circle.player + " at " + circle.min + ":" + (circle.sec < 10 ? "0" + circle.sec : circle.sec);
          var maxTextWidth = Math.max(self.measureText(textLine1, 14), self.measureText(textLine2, 14));
          var widthTooltip = maxTextWidth + 60;
          var height = self.type != "game" ? 70 : 110;
          height = self.team ? 90 : height;

          let tooltipText = self.type != "game" ? "<b>" + circle.game + "</b></br>" +
                  (self.team ? `${circle.playerFrom}</br>` : '') +
                  circle.xG.toFixed(2) + " xA</br>" +
                  self.getTypeOfPass(circle.type) + " to "+ circle.player + " at " + circle.min + ":" + (circle.sec < 10 ? "0" + circle.sec : circle.sec)
          : "<b>" + circle.game + "</b></br>" +
                  circle.xG.toFixed(2) + " xA</br>" +
                  self.getTypeOfPass(circle.type) + "</br>" +
                  circle.playerFrom + " to "+ circle.player + "</br>at " + circle.min + ":" + (circle.sec < 10 ? "0" + circle.sec : circle.sec);

          var shotId = document.getElementById("pass" + i + "-" + circle.teamName);
          var circleRadio = Math.ceil(shotId.getAttribute('r'));
          let tooltipX = self.$vuetify.breakpoint.mobile ? (window.innerWidth/2) - (widthTooltip/2): shotId.getBoundingClientRect().left + circleRadio - widthTooltip / 2
          let tooltipY = self.$vuetify.breakpoint.mobile ? d3.select(`#${self.rootElementId}`).node().getBoundingClientRect().top + window.scrollY + (height/2) : (shotId.getBoundingClientRect().top + window.scrollY +  circleRadio - height - 30)
          d3.select("body > .tooltip")
            .style("width", widthTooltip + "px")
            .style("height", height + "px")
            .style("left", tooltipX + "px")
            .style("top", tooltipY + "px")
            .style("display", "inline-block")
            .style("font-size", "14px")
            .html(tooltipText);
        })
        .on('mouseout',function() {
          d3.select(this)
            .transition()
            .duration(200)
            .attr('r', radius);
          d3.select("body > .tooltip").style("display", "none");
        });
    },
    drawXALine(svg, x0, y0, x1, y1, width, xa, strokeColor = "black", opacity = 1) {
      var self = this;
      svg.append("line")
        .attr("x1", x0)
        .attr("y1", y0)
        .attr("x2", x1)
        .attr("y2", y1)
        .attr("stroke-width", 0.002 * this.SVG_WIDTH)
        .style("opacity", 1)
        .attr("stroke", "#" + self.colorScale(0.5, 0, xa > 0.5 ? 0.5 : xa, 100).substring(1));
    },
    renderXACircles(svg, chartSizeRate, chartWidth, chartHeight, circles, teamColor, strokeColor, tooltip, isPlayer) {

      let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
      let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);

      let radius = d3.scale.linear().domain([0, 1]).range([chartWidth * 0.0055, chartWidth * 0.024]);
      for (let i in circles) {
        let sx;
        let sy;
        if (isPlayer) {
          sx = x(circles[i].y_end);
          sy = y(2 * circles[i].x_end - 100);
        } else if (circles[i].xG) {
          sx = circles[i].homeTeam ? x(circles[i].x_end) : x(100 - circles[i].x_end);
          sy = circles[i].homeTeam ? y(100 - circles[i].y_end) : y(circles[i].y_end);
        } else {
          sx = x(100 - circles[i].x_end);
          sy = y(circles[i].y_end);
        }
        this.drawXACircle(svg, chartSizeRate, sx, sy, circles[i], i, radius(circles[i].xG), circles[i].xG, tooltip, circles[i].type === "GOAL" ? true : false);
      }
    },
    getDistinctPlayerLegendTeamIds(seasons) {
      if (this.team) {
        const id = this.teamId || (seasons && seasons.length > 0 ? seasons[0].teamId : 0);
        return id ? [id] : [];
      }
      const seen = new Set();
      const ordered = [];
      for (const s of seasons || []) {
        const tid = s && s.teamId;
        if (tid && !seen.has(tid)) {
          seen.add(tid);
          ordered.push(tid);
          if (ordered.length >= 3) break;
        }
      }
      return ordered;
    },
    async printPlayerXALegend(svg, pitchWidth, margin, shots, seasons, playerName, language = 'en') {

      const numberOfPasses = shots.length;
      const xA = shots.map(shot => shot.xG).reduce((a, b) => a + b, 0);
      const assists = shots.filter(p => p.type === "GOAL").length;
      const noGamesSelected = this.gameIds && this.gameIds.length === 0;
      const games = noGamesSelected ? 0 : (this.gameIds.length ? this.gameIds.length : seasons.map(s => s.gamesPlayed).reduce((a, b) => a + b, 0));
      const minutes = noGamesSelected ? 0 : (this.minsPlayed > 0 ? this.minsPlayed : seasons.map(s => s.minutesPlayed).reduce((a, b) => a + b, 0));
      let xADecimals = 1;

      const rectW = 0.72 * pitchWidth;
      const rectH = 0.168 * pitchWidth;
      const rectX = (pitchWidth / 2 - rectW / 2) + margin.left;
      const rectY = pitchWidth * 0.577 + margin.top;

      this.drawRectangle(svg, rectX, rectY, rectW, rectH, "#1a1a1a", 0.92, "xaPlayerLegendRect");

      const assistsLabel = this.teamOrOpponent
        ? Dictionary[this.language]["team"]["overviewTab"]["stats"]["assists"]
        : Dictionary[this.language]["team"]["overviewTab"]["stats"]["assistsConceded"];
      const xALabel = this.teamOrOpponent ? "xA" : Dictionary[this.language]["team"]["overviewTab"]["stats"]["xAConceded"];

      const crestTeamIds = this.getDistinctPlayerLegendTeamIds(seasons);
      const baseCrestSize = pitchWidth * 0.054;
      const nameGap = pitchWidth * 0.014;
      const baseNameFontSize = 0.026 * pitchWidth;
      const nameWidth = this.measureText(playerName || "", baseNameFontSize);

      const isConceded = !this.teamOrOpponent;
      const seasonDisplayMode = this.team ? 'one-line' : 'two-line';
      const hasSecondSeasonLine = seasonDisplayMode === 'two-line' && !(this.gameId > 0 || this.gameName) && seasons.length >= 2;
      const singleCrestSize = hasSecondSeasonLine ? pitchWidth * 0.068 : baseCrestSize;

      let crestUnitSize;
      let crestClusterWidth;
      let crestStep = 0;
      if (crestTeamIds.length <= 1) {
        crestUnitSize = singleCrestSize;
        crestClusterWidth = crestUnitSize;
      } else {
        const overlapRatio = crestTeamIds.length === 2 ? 0.32 : 0.28;
        crestUnitSize = singleCrestSize * (crestTeamIds.length === 2 ? 0.84 : 0.74);
        crestStep = crestUnitSize * (1 - overlapRatio);
        crestClusterWidth = crestUnitSize + (crestTeamIds.length - 1) * crestStep;
      }

      const heroFontSizeCalc = pitchWidth * 0.025;
      const assistsBaseForCalc = Dictionary[this.language]["team"]["overviewTab"]["stats"]["assists"];
      const heroTextCalc = `${ assists } ${ assistsBaseForCalc }  ·  ${ xA.toFixed(xADecimals) } xA`;
      const heroWidthCalc = this.measureText(heroTextCalc, heroFontSizeCalc);

      const rawNameLineW = crestClusterWidth + nameGap + nameWidth;
      const rawHeroLineW = heroWidthCalc;
      const rawContentW = Math.max(rawNameLineW, rawHeroLineW);
      const minBoxInsetX = pitchWidth * 0.038;
      const minDividerX = rectX + rectW * 0.42;
      const maxDividerX = rectX + rectW * 0.578;
      const xaDividerGap = pitchWidth * 0.036;
      const minDividerXFromInset = rectX + minBoxInsetX + rawContentW + pitchWidth * 0.028;
      const dividerX = Math.min(maxDividerX, Math.max(minDividerX, rectX + rawContentW + xaDividerGap, minDividerXFromInset));

      const leftAvailable = dividerX - rectX;
      const symmetricLeftPad = (leftAvailable - rawContentW) / 2;
      const minInsetX = pitchWidth * 0.032;
      const minSingleCrestPad = pitchWidth * 0.056;
      const maxLeftPad = Math.max(0, leftAvailable - rawContentW - pitchWidth * 0.010);
      const targetPad = crestTeamIds.length > 1
        ? Math.max(minInsetX, symmetricLeftPad)
        : Math.max(minSingleCrestPad, symmetricLeftPad);
      const leftPad = Math.min(maxLeftPad, Math.max(minBoxInsetX, Math.min(targetPad, maxLeftPad)));

      const leftContentH = crestUnitSize + pitchWidth * 0.012 + pitchWidth * 0.030 + pitchWidth * 0.014 + (hasSecondSeasonLine ? pitchWidth * 0.010 : 0);
      const leftTopY = rectY + (rectH - leftContentH) / 2;
      const crestY = leftTopY;

      for (let idx = 0; idx < crestTeamIds.length; idx++) {
        const tid = crestTeamIds[idx];
        const cx = rectX + leftPad + idx * crestStep;
        await this.drawImageBase64Async(svg, cx, crestY, crestUnitSize, crestUnitSize,
          'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + tid + '.png', 'xaPlayerCrest-' + tid);
      }

      const nameX = rectX + leftPad + crestClusterWidth + nameGap;
      const nameY = crestY + pitchWidth * 0.004;

      svg.append("text")
        .attr("x", nameX)
        .attr("y", nameY)
        .attr("text-anchor", "start")
        .attr("dy", "0.8em")
        .attr("font-weight", 600)
        .style("font-size", baseNameFontSize + "px")
        .style("font-family", this.fontFamily)
        .style("fill", "white")
        .attr("class", "playerXAName")
        .text(playerName);

      const seasonFontSize = pitchWidth * 0.015;
      const seasonLineH = pitchWidth * 0.016;
      const seasonDividerGap = pitchWidth * 0.030;
      const maxSeasonRaw = Math.max(0, dividerX - nameX - seasonDividerGap);
      const maxSeasonWidth = maxSeasonRaw * 0.92;
      let seasonLines = [];

      if (this.gameId > 0 || this.gameName) {
        seasonLines.push(this.truncateText(this.gameName || '', seasonFontSize, maxSeasonWidth));
        xADecimals = 2;
      } else if (seasons.length > 0) {
        const names = seasons.map(s => s.seasonAlias || s.seasonName);

        if (seasonDisplayMode === 'two-line') {
          if (names.length === 1) {
            seasonLines.push(this.truncateText(names[0], seasonFontSize, maxSeasonWidth));
          } else if (names.length === 2) {
            seasonLines.push(this.truncateText(names[0], seasonFontSize, maxSeasonWidth));
            seasonLines.push(this.truncateText(names[1], seasonFontSize, maxSeasonWidth));
          } else {
            const prefix1 = names[0] + ', ';
            const prefixW1 = this.measureText(prefix1, seasonFontSize);
            const line1 = prefix1 + this.truncateText(names[1], seasonFontSize, maxSeasonWidth - prefixW1);
            seasonLines.push(line1);

            if (names.length >= 3) {
              let extra = '';
              if (names.length >= 4) {
                extra = ` +${names.length - 2}`;
              } else if (names.length === 3) {
                const suffixOne = ' +1';
                const wSuffix = this.measureText(suffixOne, seasonFontSize);
                const w3 = this.measureText(names[2], seasonFontSize);
                if (w3 + wSuffix > maxSeasonWidth * 0.86) {
                  extra = suffixOne;
                }
              }
              const availW2 = extra ? maxSeasonWidth - this.measureText(extra, seasonFontSize) : maxSeasonWidth;
              if (names.length >= 4) {
                const prefix2 = names[2] + ', ';
                const prefixW2 = this.measureText(prefix2, seasonFontSize);
                const line2 = prefix2 + this.truncateText(names[3], seasonFontSize, availW2 - prefixW2) + extra;
                seasonLines.push(line2);
              } else {
                const line2 = this.truncateText(names[2], seasonFontSize, availW2) + extra;
                seasonLines.push(line2);
              }
            }
          }
        } else {
          const extraCount = names.length > 2 ? ` +${names.length - 2}` : '';
          const maxNames = Math.min(names.length, 2);
          let joined = names.slice(0, maxNames).join(', ');
          let seasonText = joined + extraCount;

          let seasonWidth = this.measureText(seasonText, seasonFontSize);
          if (seasonWidth > maxSeasonWidth && maxNames > 1) {
            const newExtra = ` +${names.length - 1}`;
            seasonText = names[0] + newExtra;
            seasonWidth = this.measureText(seasonText, seasonFontSize);
          }
          if (seasonWidth > maxSeasonWidth) {
            seasonText = this.truncateText(seasonText, seasonFontSize, maxSeasonWidth);
          }
          seasonLines.push(seasonText);
        }
      }

      seasonLines = seasonLines.map(line => this.truncateSeasonLegendLine(line, seasonFontSize, maxSeasonWidth));

      seasonLines.forEach((line, i) => {
        svg.append("text")
          .attr("x", nameX)
          .attr("y", nameY + pitchWidth * 0.030 + i * seasonLineH)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 400)
          .style("font-size", seasonFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "rgba(255,255,255,0.85)")
          .attr("class", "seasonText")
          .text(line);
      });

      const heroFontSize = pitchWidth * 0.025;
      const heroY = crestY + crestUnitSize + pitchWidth * 0.008 + (hasSecondSeasonLine ? pitchWidth * 0.010 : 0);

      if (isConceded) {
        const assistsBaseLabel = Dictionary[this.language]["team"]["overviewTab"]["stats"]["assists"];
        const concededSuffix = assistsLabel.replace(assistsBaseLabel, '').trim() || 'Conceded';

        const assistsPart = `${ assists } ${ assistsBaseLabel }`;
        const xaPart = `${ xA.toFixed(xADecimals) } xA`;
        const heroLine1 = `${ assistsPart }  ·  ${ xaPart }`;

        svg.append("text")
          .attr("x", rectX + leftPad)
          .attr("y", heroY)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 700)
          .style("font-size", heroFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .attr("class", "playerXAandAssists")
          .text(heroLine1);

        const concededFontSize = pitchWidth * 0.014;
        const assistsPartWidth = this.measureText(assistsPart, heroFontSize);
        const dotWidth = this.measureText('  ·  ', heroFontSize);

        svg.append("text")
          .attr("x", rectX + leftPad)
          .attr("y", heroY + pitchWidth * 0.027)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 500)
          .style("font-size", concededFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .text(concededSuffix);

        svg.append("text")
          .attr("x", rectX + leftPad + assistsPartWidth + dotWidth - pitchWidth * 0.030)
          .attr("y", heroY + pitchWidth * 0.027)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 500)
          .style("font-size", concededFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .text(concededSuffix);
      } else {
        const heroLine1 = `${ assists } ${ assistsLabel }  ·  ${ xA.toFixed(xADecimals) } ${ xALabel }`;

        svg.append("text")
          .attr("x", rectX + leftPad)
          .attr("y", heroY)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 700)
          .style("font-size", heroFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .attr("class", "playerXAandAssists")
          .text(heroLine1);
      }

      // Vertical divider
      svg.append("line")
        .attr("x1", dividerX)
        .attr("y1", rectY + pitchWidth * 0.022)
        .attr("x2", dividerX)
        .attr("y2", rectY + rectH - pitchWidth * 0.022)
        .attr("stroke", "rgba(255,255,255,0.5)")
        .attr("stroke-width", 1);

      // Right section: stats, xA breakdown, and filters in 3 columns
      const openPlayStrategyIds = [22, 23, 123, 215];
      const setPieceStrategyIds = [26, 24, 25, 124, 160, 279];
      const xAOpenPlay = shots.filter(p => openPlayStrategyIds.includes(p.strategyId)).map(shot => shot.xG).reduce((a, b) => a + b, 0);
      const xASetPiece = shots.filter(p => setPieceStrategyIds.includes(p.strategyId)).map(shot => shot.xG).reduce((a, b) => a + b, 0);

      const rightSectionW = rectX + rectW - dividerX;
      const rightPad = pitchWidth * 0.026;
      const rightX = dividerX + rightPad;
      const col2X = dividerX + rightSectionW * 0.36;
      const col3X = dividerX + rightSectionW * 0.66;

      const labelFontSize = pitchWidth * 0.012;
      const valueFontSize = pitchWidth * 0.019;
      const rowH = pitchWidth * 0.042;
      const totalRows = 3;
      const rightContentH = totalRows * rowH;
      const startY = rectY + (rectH - rightContentH) / 2;

      const gamesLabel = this.team
        ? Dictionary[this.language]["table"]["history"]["games"]
        : Dictionary[this.language]["table"]["history"]["games"] + ' / ' + Dictionary[this.language]["table"]["roster"]["minutes"];
      const gamesValue = (games === 1 && this.gameName)
        ? this.gameName
        : (this.team ? `${games}` : `${games} / ${minutes}`);
      const xAPerPass = numberOfPasses > 0 ? (xA / numberOfPasses).toFixed(2) : '0.00';
      const statsCol1 = [
        { label: gamesLabel, value: gamesValue },
        { label: Dictionary[this.language]["keyPassMap"]["chart"]["keyPasses"], value: `${numberOfPasses}` },
        { label: Dictionary[this.language]["keyPassMap"]["chart"]["xAPerPass"], value: xAPerPass }
      ];

      statsCol1.forEach((stat, i) => {
        const y = startY + i * rowH;
        svg.append("text")
          .attr("x", rightX)
          .attr("y", y)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 400)
          .style("font-size", labelFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "rgba(255,255,255,0.7)")
          .text(stat.label);
        svg.append("text")
          .attr("x", rightX)
          .attr("y", y + pitchWidth * 0.015)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 600)
          .style("font-size", valueFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .text(stat.value);
      });

      const statsCol2 = [
        { label: Dictionary[this.language]["keyPassMap"]["chart"]["xAOpenPlay"], value: xAOpenPlay.toFixed(xADecimals) },
        { label: Dictionary[this.language]["keyPassMap"]["chart"]["xASetPiece"], value: xASetPiece.toFixed(xADecimals) }
      ];

      statsCol2.forEach((stat, i) => {
        const y = startY + i * rowH;
        svg.append("text")
          .attr("x", col2X)
          .attr("y", y)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 400)
          .style("font-size", labelFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "rgba(255,255,255,0.7)")
          .text(stat.label);
        svg.append("text")
          .attr("x", col2X)
          .attr("y", y + pitchWidth * 0.015)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 600)
          .style("font-size", valueFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .text(stat.value);
      });

      const typesLabel = Dictionary[this.language]["finishingMap"]["typesOfShot"];
      const strategyLabel = Dictionary[this.language]["finishingMap"]["shotStrategy"];
      const bodyPartLabel = Dictionary[this.language]["finishingMap"]["bodyPart"];
      const allLabel = Dictionary[this.language]["general"] && Dictionary[this.language]["general"]["all"] ? Dictionary[this.language]["general"]["all"] : 'All';
      const maxFilterWidth = rectX + rectW - col3X - pitchWidth * 0.034;
      const filtersData = [
        { label: typesLabel, value: this.filterShotTypeText || allLabel },
        { label: strategyLabel, value: this.filterShotStrategyText || allLabel },
        { label: bodyPartLabel, value: this.filterBodyPartText || allLabel }
      ];

      filtersData.forEach((filter, i) => {
        const y = startY + i * rowH;
        svg.append("text")
          .attr("x", col3X)
          .attr("y", y)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 400)
          .style("font-size", labelFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "rgba(255,255,255,0.7)")
          .text(filter.label);

        const plusMatch = filter.value.match(/^(.+?)(\s\+\d+)$/);
        let displayValue;
        if (plusMatch) {
          const suffix = plusMatch[2];
          const suffixW = this.measureText(suffix, valueFontSize);
          const namePartMax = maxFilterWidth - suffixW;
          displayValue = this.truncateText(plusMatch[1], valueFontSize, namePartMax) + suffix;
        } else {
          displayValue = this.truncateText(filter.value, valueFontSize, maxFilterWidth);
        }
        svg.append("text")
          .attr("x", col3X)
          .attr("y", y + pitchWidth * 0.018)
          .attr("text-anchor", "start")
          .attr("dy", "0.8em")
          .attr("font-weight", 600)
          .style("font-size", valueFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .text(displayValue);
      });

      const bottomInfoY = rectY + rectH - pitchWidth * 0.032;
      const bottomInfoFontSize = pitchWidth * 0.011;

      const minutesRangeText = this.filterMinutesText || this.filterMinutesRange;
      if (minutesRangeText) {
        svg.append("text")
          .attr("x", rectX + rectW - pitchWidth * 0.038)
          .attr("y", bottomInfoY)
          .attr("text-anchor", "end")
          .attr("dy", "0.8em")
          .attr("font-weight", 400)
          .style("font-size", bottomInfoFontSize + "px")
          .style("font-family", this.fontFamily)
          .style("fill", "white")
          .text(minutesRangeText);
      }

    },
    printGameXALegend(svg, pitchWidth, pitchHeight, margin, shots) {
      let homeShots = shots.filter(s => s.homeTeam);
      let awayShots = shots.filter(s => !s.homeTeam);

      let homeAssists = homeShots.filter(d => d.type === "GOAL").length;
      let awayAssists = awayShots.filter(d => d.type === "GOAL").length;

      let homeXA = homeShots.map(s => s.xG).reduce((a, b) => a + b, 0);
      let awayXA = awayShots.map(s => s.xG).reduce((a, b) => a + b, 0);

      const rectangleSize = 0.28 * pitchWidth;
      const homeDiff = (homeAssists - homeXA).toFixed(1);
      const awayDiff = (awayAssists - awayXA).toFixed(1);
      const homeDiffStr = `(${homeAssists > homeXA ? '+' : ''}${homeDiff})`;
      const awayDiffStr = `(${awayAssists > awayXA ? '+' : ''}${awayDiff})`;

      let xaLegend = this.currentUserLanguage === 'en' ? "Sum of xA" : "Suma de xA";

      this.drawRectangle(svg,
        (pitchWidth / 2 - rectangleSize / 2) + margin.left,
        margin.top,
        rectangleSize,
        rectangleSize * 0.28,
        "black",
        0.85,
        "xaLegendRect");
      this.writeText(svg, pitchWidth / 2 + margin.left, 0.008 * pitchWidth + margin.top, "middle", (0.018 * pitchWidth) + "px", xaLegend);
      const valY = 0.032 * pitchWidth + margin.top;
      const valFont = (0.035 * pitchWidth) + "px";
      const diffFont = (0.016 * pitchWidth) + "px";
      const cx = pitchWidth / 2 + margin.left;
      this.writeText(svg, cx, valY, "middle", valFont,
        `${homeXA.toFixed(1)} · ${awayXA.toFixed(1)}`);
      this.writeText(svg, cx - 0.065 * pitchWidth, valY, "end", diffFont, homeDiffStr, homeAssists > homeXA ? "green" : "red");
      this.writeText(svg, cx + 0.065 * pitchWidth, valY, "start", diffFont, awayDiffStr, awayAssists > awayXA ? "green" : "red");
    },
    printGameBottomLegend(svg, pitchWidth, pitchHeight, margin, shots) {
      const homeShots = shots.filter(s => s.homeTeam);
      const awayShots = shots.filter(s => !s.homeTeam);

      const homeXA = homeShots.map(s => s.xG).reduce((a, b) => a + b, 0);
      const awayXA = awayShots.map(s => s.xG).reduce((a, b) => a + b, 0);
      const homePassCount = homeShots.length;
      const awayPassCount = awayShots.length;
      const homeXAPerPass = homePassCount > 0 ? (homeXA / homePassCount) : 0;
      const awayXAPerPass = awayPassCount > 0 ? (awayXA / awayPassCount) : 0;

      const homeXAPlayers = this.getPlayersWithHighestXG(homeShots);
      const awayXAPlayers = this.getPlayersWithHighestXG(awayShots);

      const rectW = pitchWidth * 0.58;
      const rectH = pitchWidth * 0.1;
      const rectX = (pitchWidth / 2 - rectW / 2) + margin.left;
      const rectY = pitchHeight + margin.top - rectH - pitchWidth * 0.01;

      this.drawRectangle(svg, rectX, rectY, rectW, rectH, "#1a1a1a", 0.92, "gameBottomRect");

      const colPad = pitchWidth * 0.012;
      const leftColX = rectX + colPad;
      const rightColEnd = rectX + rectW - colPad;
      const centerX = rectX + rectW / 2;

      const vDivLeftX = centerX - pitchWidth * 0.12;
      const vDivRightX = centerX + pitchWidth * 0.12;

      const playerFont = (pitchWidth * 0.012) + "px";
      const topY = rectY + pitchWidth * 0.01;
      const playerNameW = pitchWidth * 0.1;
      const playerXAOffset = pitchWidth * 0.13;

      const homePlayerStart = vDivLeftX - playerXAOffset - pitchWidth * 0.015;
      const awayPlayerStart = vDivRightX + pitchWidth * 0.015;

      homeXAPlayers.forEach((p, i) => {
        const y = topY + i * pitchWidth * 0.017;
        this.writeText(svg, homePlayerStart, y, "start", playerFont, this.truncateText(p.playerFrom, pitchWidth * 0.012, playerNameW), "white");
        this.writeText(svg, homePlayerStart + playerXAOffset, y, "end", playerFont, p.xG.toFixed(2), "rgba(255,255,255,0.8)");
      });

      awayXAPlayers.forEach((p, i) => {
        const y = topY + i * pitchWidth * 0.017;
        this.writeText(svg, awayPlayerStart, y, "start", playerFont, this.truncateText(p.playerFrom, pitchWidth * 0.012, playerNameW), "white");
        this.writeText(svg, awayPlayerStart + playerXAOffset, y, "end", playerFont, p.xG.toFixed(2), "rgba(255,255,255,0.8)");
      });

      const vDivTop = rectY + pitchWidth * 0.006;
      const vDivBottom = topY + 3 * pitchWidth * 0.017;
      svg.append("line")
        .attr("x1", vDivLeftX).attr("y1", vDivTop)
        .attr("x2", vDivLeftX).attr("y2", vDivBottom)
        .attr("stroke", "rgba(255,255,255,0.25)").attr("stroke-width", 1);
      svg.append("line")
        .attr("x1", vDivRightX).attr("y1", vDivTop)
        .attr("x2", vDivRightX).attr("y2", vDivBottom)
        .attr("stroke", "rgba(255,255,255,0.25)").attr("stroke-width", 1);

      const statLabelFont = (pitchWidth * 0.012) + "px";
      const statValueFont = (pitchWidth * 0.018) + "px";

      const keyPassesLabel = Dictionary[this.language]["keyPassMap"]["chart"]["keyPasses"];
      const xaPerPassLabel = Dictionary[this.language]["keyPassMap"]["chart"]["xAPerPass"];

      const homeValX = centerX - pitchWidth * 0.055;
      const awayValX = centerX + pitchWidth * 0.055;

      const passesY = topY;
      this.writeText(svg, homeValX, passesY, "end", statValueFont, `${homePassCount}`, "white");
      this.writeText(svg, centerX, passesY + pitchWidth * 0.003, "middle", statLabelFont, keyPassesLabel, "rgba(255,255,255,0.7)");
      this.writeText(svg, awayValX, passesY, "start", statValueFont, `${awayPassCount}`, "white");

      const xapsY = passesY + pitchWidth * 0.025;
      this.writeText(svg, homeValX, xapsY, "end", statValueFont, homeXAPerPass.toFixed(2), "white");
      this.writeText(svg, centerX, xapsY + pitchWidth * 0.003, "middle", statLabelFont, xaPerPassLabel, "rgba(255,255,255,0.7)");
      this.writeText(svg, awayValX, xapsY, "start", statValueFont, awayXAPerPass.toFixed(2), "white");

      const hDivY = vDivBottom + pitchWidth * 0.004;
      svg.append("line")
        .attr("x1", rectX + colPad)
        .attr("y1", hDivY)
        .attr("x2", rectX + rectW - colPad)
        .attr("y2", hDivY)
        .attr("stroke", "rgba(255,255,255,0.25)")
        .attr("stroke-width", 1);

      const filterLabelFont = (pitchWidth * 0.01) + "px";
      const filterValueFont = (pitchWidth * 0.012) + "px";
      const filterY = hDivY + pitchWidth * 0.006;
      const allLabel = Dictionary[this.language]["general"] && Dictionary[this.language]["general"]["all"] ? Dictionary[this.language]["general"]["all"] : 'All';

      const typesLabel = Dictionary[this.language]["finishingMap"]["typesOfShot"];
      const strategyLabel = Dictionary[this.language]["finishingMap"]["shotStrategy"];
      const bodyPartLabel = Dictionary[this.language]["finishingMap"]["bodyPart"];

      const timeValue = this.filterTimeText || (Dictionary[this.language]["videoEvents"] && Dictionary[this.language]["videoEvents"]["fullGame"]) || 'Full Game';

      const filters = [
        { label: "Time", value: timeValue },
        { label: typesLabel, value: this.filterShotTypeText || allLabel },
        { label: strategyLabel, value: this.filterShotStrategyText || allLabel },
        { label: bodyPartLabel, value: this.filterBodyPartText || allLabel }
      ];

      const filterStartX = homePlayerStart;
      const filterTotalW = rightColEnd - filterStartX;
      const filterItemW = filterTotalW / filters.length;

      filters.forEach((f, i) => {
        const x = filterStartX + i * filterItemW;
        this.writeText(svg, x, filterY, "start", filterLabelFont, f.label, "rgba(255,255,255,0.6)");
        this.writeText(svg, x, filterY + pitchWidth * 0.012, "start", filterValueFont,
          this.truncateText(f.value, pitchWidth * 0.012, filterItemW * 0.9), "white");
      });
    },
    getPlayersWithHighestXG(shots) {
      const sumXGShots = shots.reduce(function(acc, val){
        var o = acc.filter(function(obj){
            return obj.playerFrom === val.playerFrom;
        }).pop() || {playerFrom: val.playerFrom, xG: 0};

        o.xG += val.xG;
        const index = acc.findIndex(p => p.playerFrom === o.playerFrom)
        if (index === -1)
          acc.push(o);
        else
          acc[index] = o;
        return acc;
      },[]);
      // Sort, take out players with xG < 0.1 and return first three
      return sumXGShots.sort((a, b) => b.xG - a.xG).filter(p => p.xG >= 0.1).slice(0, 3);
    },
    printGameLegend(svg, pitchSize, pitchHeight, margin, homeTeamColor, awayTeamColor, gameData) {
      let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      let secondHomeTeamColor = this.homeBorderColor;
      let neutralColorsAway = getNeutralColors('away');
      let secondAwayTeamColor = this.game ? neutralColorsAway[1] : this.awayBorderColor;

      let homeScore = this.game.homeTeamScore;
      let awayScore = this.game.awayTeamScore;

      let homeTeamCrestXFactor;
      if (this.isVertical) {
        homeTeamCrestXFactor = this.isMobile ? 0.12 : 0.08;
      }
      else {
        homeTeamCrestXFactor = this.isMobile ? 0.09 : 0.07;
      }
      let homeCrestX = homeTeamCrestXFactor * pitchSize + margin.left

      let rectHome = this.drawRectangle(svg,
          homeCrestX - 16,
        margin.top - 8,
          16,
          (0.075 * pitchHeight) + 5,
          homeTeamColor.replace("0x", "#"), 0.9, "gameLegend1");
                  rectHome
        .style("border-radius",'3px')
        .style("border",`1px solid ${secondHomeTeamColor}`)
        .style("opacity",0.95)
        .style("stroke-width",1)
        .style("stroke",`${secondHomeTeamColor}`)
        .attr("rx",3)
        .attr("ry",3)

      
      let homeTeamNameXFactor;
      if (this.isVertical) {
        homeTeamNameXFactor = this.isMobile ? 0.18 : 0.14;
      }
      else {
        homeTeamNameXFactor = this.isMobile ? 0.13 : 0.115;
      }

      this.drawImageBase64(svg,
        homeTeamCrestXFactor * pitchSize + margin.left,
        (this.isVertical ? 0.03 : 0.02) * pitchHeight + margin.top,
        0.05 * pitchSize,
        0.05 * pitchHeight,
        'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.homeTeamId + '.png', 'homeCrest');
      this.writeText(svg,
        homeTeamNameXFactor * pitchSize + margin.left,
        (this.isVertical ? 0.05 : 0.03) * pitchHeight + margin.top,
        "start",
        ((this.isVertical ? 0.024 : 0.022) * pitchSize) + "px",
        `${ this.homeTeamName } • ` + homeScore, textColor);

      let rectAway = this.drawRectangle(svg,
        0.935 * pitchSize + margin.left,
        margin.top - 8,
        16,
        (0.075 * pitchHeight) + 5,
        awayTeamColor.replace("0x", "#"), 0.9, "gameLegend1");
      rectAway
      .style("border-radius",'3px')
      .style("border",`1px solid ${secondAwayTeamColor}`)
      .style("opacity",0.95)
      .style("stroke-width",1)
      .style("stroke",`${secondAwayTeamColor}`)
      .attr("rx",3)
      .attr("ry",3)

      this.drawImageBase64(svg,
        (this.isVertical ? 0.875 : 0.885) * pitchSize + margin.left,
        (this.isVertical ? 0.03 : 0.02) * pitchHeight + margin.top,
        0.05 * pitchSize,
        0.05 * pitchHeight,
        'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.awayTeamId + '.png');
      this.writeText(svg,
        (this.isVertical ? 0.865 : 0.89) * pitchSize + margin.left,
        (this.isVertical ? 0.05 : 0.03) * pitchHeight + margin.top,
        "end",
        ((this.isVertical ? 0.024 : 0.022) * pitchSize) + "px",
        awayScore + ` • ${ this.awayTeamName }`, textColor);
    },
    renderBoxes(svg, svgWidth, pitchHeight, margin, shots, language = 'en') {
      let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([this.PITCH_WIDTH, 0]);
      let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([this.PITCH_HEIGHT, 0]);

      let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
      const passLegend = language === 'en' ? 'passes' : 'pases';

      var shadowScale = d3.scale.linear().domain([0, shots.length]).range([0.1, 0.7]);

      _.forEach(this.currentUserAreas,(a,i) => {
        let areaEvents = _.filter(shots,(e) => {
          let target = e.startAreaId;
          let areaContain = a.id === target;
          return areaContain
        })

        if(!_.isEmpty(areaEvents)){
          const verticalFactor = 2;
          let rectX = x(a.y2);
          let rectY = y(100 - (100 - a.x2) * verticalFactor);
          let rectWidth = x(a.y1) - x(a.y2);
          let rectHeight = (y(a.x1) - y(a.x2)) * verticalFactor;

          this.drawRectangle(svg,
              rectX,
              rectY,
              rectWidth,
              rectHeight, "red", shadowScale(areaEvents.length),`area-${i}-${a.id}`);
          
          let centerX = rectX + (rectWidth/2);
          let centerY = rectY + (rectHeight/2);

          this.writeText(svg, centerX , centerY - (0.03 * this.PITCH_HEIGHT), "middle", (0.018 * svgWidth) + "px", `${ Math.round(areaEvents.length / shots.length * 100) } %`,textColor);
          this.writeText(svg, centerX , centerY, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(areaEvents.length) } ${ passLegend })`,textColor);
        }
      })
    },
    onResize(){
      let elto =document.getElementById(this.rootElementId);
      if(elto){
        this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
      }
    }
  },
  destroyed() {
    delete this.observer;
  },
}

</script>
